import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Download,
  LineChart,
  Link2,
  Loader2,
  MoreHorizontal,
  Plus,
  X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/custom-dropdown-menu';
import { PIPEFITTER_GET_ALL_PIPE_DATA } from '../../../redux/Pipefitter/types';
import {
  extractPipeCompatibilityFromHistoryRecord,
  extractPipeCompatibilityFromReportData,
  extractPipeDataVisualizationFromReportData,
  hasCompatibilityChartData,
  listProjectReportHistory,
  type ReportHistoryListItem,
  type ReportHistoryReportData,
} from '../api/reportHistory';
import {
  getReportVersions,
  isReportVersionsFailure,
  normalizeReportVersionRow,
} from '../api/reportVersions';
import { PIPEFITTER_REPORT_TYPES } from '../constants/reportVersionTypes';
import {
  buildReportHistoryGroups,
  findGroupMatchingPipeIds,
  normalizePipeIdSetKey,
  pipeIdSetsMatch,
  type GroupedReportEntry,
  type ReportHistoryGroup,
} from '../helpers/reportHistoryGroups';
import {
  createPendingReportGeneration,
  hasActiveReportGenerations,
  isGroupReportGenerating,
  prunePendingGenerations,
  type PendingReportGeneration,
} from '../helpers/reportGenerationStatus';
import { hasProjectSummaryChartData } from '../helpers/reportHelpers';
import { PipeDataReportVisualization } from './PipeDataReportVisualization';
import { PipeCompatibility } from './PipeCompatibility';
import { PipeDataReportGenerateModal } from './PipeDataReportGenerateModal';
import { PipeCompatibilityReportGenerateModal } from './PipeCompatibilityReportGenerateModal';
import type { DashboardProject } from '../../project/types/project';
import { PIPEFITTER_FULLSCREEN_DIALOG_Z60, PIPEFITTER_FULLSCREEN_DIALOG_Z70 } from '../constants/uiConstants';

interface PipefitterReportVersionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: DashboardProject;
  projectName: string;
  projectNumber: string;
  projectId?: string | number | null;
  selectedPipe?: { pipeId: string; displayName?: string; pipeName?: string } | null;
  /** Pipe status row ids from Pipe Details selection (e.g. 1325, 1326). */
  selectedPipeStatusIds?: Array<string | number>;
  /** Bump while open to refetch report history. */
  versionsRefreshKey?: number;
}

type GenerateModalState = {
  open: boolean;
  targetIds: string[];
};

const closedGenerateModal = (): GenerateModalState => ({ open: false, targetIds: [] });

const REPORT_HISTORY_POLL_MS = 30_000;
const REPORT_HISTORY_PAGE_SIZE = 10;

type DetailView =
  | { mode: 'pipeData'; entry: GroupedReportEntry; group: ReportHistoryGroup }
  | { mode: 'pipeCompatibility'; entry: GroupedReportEntry; group: ReportHistoryGroup };

function formatGroupDate(date: Date): string {
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function hasCompatibilityData(data: Record<string, unknown> | null | undefined): boolean {
  if (!data) return false;
  return hasCompatibilityChartData(data) || hasProjectSummaryChartData(data);
}

function extractPipeCompatibilityFromBody(body: { data?: unknown }): Record<string, unknown> {
  const record = Array.isArray(body?.data) ? body.data[0] : body?.data;
  return extractPipeCompatibilityFromHistoryRecord(record);
}

function getGroupReportTitle(hasData: boolean, hasCompatibility: boolean): string {
  if (hasData && hasCompatibility) return 'Pipe Data & Compatibility Report';
  if (hasData) return 'Pipe Data Report';
  if (hasCompatibility) return 'Pipe Compatibility Report';
  return 'Report';
}

function entryHasPipeData(
  entry: GroupedReportEntry | null,
  histories: ReportHistoryListItem[]
): boolean {
  if (!entry) return false;
  const history = histories.find(
    (row) => row.id === entry.historyId || row.id === entry.docId
  );
  if (history?.reportData && extractPipeDataVisualizationFromReportData(history.reportData)) {
    return true;
  }
  return Boolean(entry.fileUrl?.trim());
}

function entryHasCompatibility(
  entry: GroupedReportEntry | null,
  histories: ReportHistoryListItem[]
): boolean {
  if (!entry || entry.reportType !== PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY) {
    return false;
  }
  const history = histories.find(
    (row) => row.id === entry.historyId || row.id === entry.docId
  );
  const compat = extractPipeCompatibilityFromReportData(history?.reportData);
  if (hasProjectSummaryChartData(compat)) return true;
  return Boolean(entry.fileUrl?.trim());
}

function resolveGroupReportAvailability(
  group: ReportHistoryGroup,
  histories: ReportHistoryListItem[]
): { hasData: boolean; hasCompatibility: boolean } {
  return {
    hasData: entryHasPipeData(group.pipeData, histories),
    hasCompatibility: entryHasCompatibility(group.pipeCompatibility, histories),
  };
}

function getGroupIconStyles(
  group: ReportHistoryGroup,
  hasData: boolean,
  hasCompatibility: boolean
): { bg: string; icon: string } {
  if (hasData && hasCompatibility) {
    return {
      bg: 'bg-[rgba(0,39,63,0.06)]',
      icon: 'text-blueprint-blue',
    };
  }
  if (hasCompatibility && !hasData) {
    return {
      bg: 'bg-[rgba(0,39,63,0.06)]',
      icon: 'text-blueprint-blue',
    };
  }
  return {
    bg: 'bg-[rgba(235,28,55,0.08)]',
    icon: 'text-hero-red',
  };
}

function hasPipeDataVisualization(reportData: ReportHistoryReportData | null | unknown): boolean {
  return extractPipeDataVisualizationFromReportData(reportData) != null;
}

function defaultExportFileName(entry: GroupedReportEntry): string {
  return entry.reportType === PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY
    ? 'pipe-compatibility-report.pdf'
    : 'pipe-data-report.pdf';
}

export const PipefitterReportVersionsModal: React.FC<PipefitterReportVersionsModalProps> = ({
  isOpen,
  onClose,
  project,
  projectName,
  projectNumber,
  projectId,
  selectedPipe = null,
  selectedPipeStatusIds = [],
  versionsRefreshKey = 0,
}) => {
  const dispatch = useDispatch();
  const [groups, setGroups] = useState<ReportHistoryGroup[]>([]);
  const [histories, setHistories] = useState<ReportHistoryListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [detailView, setDetailView] = useState<DetailView | null>(null);
  const [detailData, setDetailData] = useState<
    ReportHistoryReportData | Record<string, unknown> | null
  >(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [pipeDataGenerate, setPipeDataGenerate] = useState<GenerateModalState>(closedGenerateModal);
  const [compatibilityGenerate, setCompatibilityGenerate] =
    useState<GenerateModalState>(closedGenerateModal);
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  const [pendingGenerations, setPendingGenerations] = useState<PendingReportGeneration[]>([]);

  const selectionIds = useMemo(
    () => selectedPipeStatusIds.map(String).filter(Boolean),
    [selectedPipeStatusIds]
  );

  const matchingGroup = useMemo(
    () => findGroupMatchingPipeIds(groups, selectionIds),
    [groups, selectionIds]
  );

  const otherGroups = useMemo(() => {
    if (!matchingGroup || !selectionIds.length) return groups;
    return groups.filter((group) => group.key !== matchingGroup.key);
  }, [groups, matchingGroup, selectionIds.length]);

  const rebuildGroups = useCallback(
    async (historyRows: ReportHistoryListItem[]) => {
      const [pipeDataEnvelope, compatibilityEnvelope] = await Promise.all([
        getReportVersions({
          projectNumber,
          reportType: PIPEFITTER_REPORT_TYPES.PIPE_DATA,
        }),
        getReportVersions({
          projectNumber,
          reportType: PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY,
        }),
      ]);

      if (isReportVersionsFailure(pipeDataEnvelope) && isReportVersionsFailure(compatibilityEnvelope)) {
        setGroups([]);
        setLoadError(
          pipeDataEnvelope.message ||
            compatibilityEnvelope.message ||
            'Could not load report history.'
        );
        return false;
      }

      const normalizeVersions = (envelope: Awaited<ReturnType<typeof getReportVersions>>) => {
        if (isReportVersionsFailure(envelope)) return [];
        const rawList = envelope.data?.versions;
        if (!Array.isArray(rawList)) return [];
        return rawList
          .map(normalizeReportVersionRow)
          .filter((row) => row.docId > 0 || row.fileUrl)
          .sort((a, b) => b.docRef - a.docRef);
      };

      const pipeDataVersions = normalizeVersions(pipeDataEnvelope);
      const compatibilityVersions = normalizeVersions(compatibilityEnvelope);
      setPendingGenerations((prev) => prunePendingGenerations(prev, historyRows));
      setGroups(buildReportHistoryGroups(historyRows, pipeDataVersions, compatibilityVersions));
      return true;
    },
    [projectNumber]
  );

  const loadHistory = useCallback(async (options?: { silent?: boolean }) => {
    if (!projectNumber) {
      setGroups([]);
      setHistories([]);
      setHasMoreHistory(false);
      setLoadError('Project number is required.');
      return;
    }

    if (!options?.silent) {
      setLoading(true);
    }
    setLoadError(null);

    try {
      const page = await listProjectReportHistory(
        projectNumber,
        projectId ?? undefined,
        selectedPipe?.pipeId,
        { skip: 0, limit: REPORT_HISTORY_PAGE_SIZE }
      );
      setHistories(page);
      setHasMoreHistory(page.length >= REPORT_HISTORY_PAGE_SIZE);
      await rebuildGroups(page);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setGroups([]);
      setHistories([]);
      setHasMoreHistory(false);
      setLoadError(err?.response?.data?.message || err?.message || 'Could not load report history.');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [projectId, projectNumber, rebuildGroups, selectedPipe?.pipeId]);

  const loadMoreHistory = useCallback(async () => {
    if (!projectNumber || loadingMore || !hasMoreHistory) return;

    setLoadingMore(true);
    setLoadError(null);
    try {
      const page = await listProjectReportHistory(
        projectNumber,
        projectId ?? undefined,
        selectedPipe?.pipeId,
        { skip: histories.length, limit: REPORT_HISTORY_PAGE_SIZE }
      );
      const merged = [...histories];
      const seen = new Set(histories.map((row) => row.id));
      for (const row of page) {
        if (!seen.has(row.id)) {
          seen.add(row.id);
          merged.push(row);
        }
      }
      setHistories(merged);
      setHasMoreHistory(page.length >= REPORT_HISTORY_PAGE_SIZE);
      await rebuildGroups(merged);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setLoadError(err?.response?.data?.message || err?.message || 'Could not load more report history.');
    } finally {
      setLoadingMore(false);
    }
  }, [
    hasMoreHistory,
    histories,
    loadingMore,
    projectId,
    projectNumber,
    rebuildGroups,
    selectedPipe?.pipeId,
  ]);

  const handleReportGenerationStarted = useCallback(
    (reportType: PendingReportGeneration['reportType'], pipeStatusIds: string[]) => {
      const entry = createPendingReportGeneration(reportType, pipeStatusIds);
      if (!entry) return;
      setPendingGenerations((prev) => {
        if (prev.some((item) => item.reportType === entry.reportType && item.pipeIdsKey === entry.pipeIdsKey)) {
          return prev.map((item) =>
            item.reportType === entry.reportType && item.pipeIdsKey === entry.pipeIdsKey
              ? { ...item, startedAtMs: entry.startedAtMs }
              : item
          );
        }
        return [...prev, entry];
      });
      setLocalRefreshKey((key) => key + 1);
      window.setTimeout(() => {
        void loadHistory({ silent: true });
      }, 2500);
    },
    [loadHistory]
  );

  useEffect(() => {
    if (!isOpen) return;
    void loadHistory();
  }, [isOpen, loadHistory, versionsRefreshKey, localRefreshKey]);

  const shouldPollReportHistory = useMemo(
    () => hasActiveReportGenerations(histories, pendingGenerations),
    [histories, pendingGenerations]
  );

  useEffect(() => {
    if (!isOpen || !shouldPollReportHistory) return;
    const timer = window.setInterval(() => {
      void loadHistory({ silent: true });
    }, REPORT_HISTORY_POLL_MS);
    return () => window.clearInterval(timer);
  }, [isOpen, shouldPollReportHistory, loadHistory]);

  useEffect(() => {
    setPendingGenerations((prev) => prunePendingGenerations(prev, histories));
  }, [histories]);

  useEffect(() => {
    if (!isOpen) {
      setDetailView(null);
      setDetailData(null);
      setDetailLoading(false);
      setDetailError(null);
      setIsExporting(false);
      setGroups([]);
      setHistories([]);
      setLoadError(null);
      setPendingGenerations([]);
      setPipeDataGenerate(closedGenerateModal());
      setCompatibilityGenerate(closedGenerateModal());
    }
  }, [isOpen]);

  const findHistoryForEntry = useCallback(
    (entry: GroupedReportEntry) =>
      histories.find((row) => row.id === entry.historyId || row.id === entry.docId) ?? null,
    [histories]
  );

  const resolveCompatibilityFromHistories = useCallback(
    (entry: GroupedReportEntry): Record<string, unknown> | null => {
      const history = findHistoryForEntry(entry);
      const compat = extractPipeCompatibilityFromReportData(history?.reportData);
      return compat && hasCompatibilityData(compat) ? compat : null;
    },
    [findHistoryForEntry]
  );

  const groupReportAvailability = useCallback(
    (group: ReportHistoryGroup) => resolveGroupReportAvailability(group, histories),
    [histories]
  );

  const loadPipeDataDetail = useCallback(
    (entry: GroupedReportEntry) => {
      const cached = findHistoryForEntry(entry);
      const visualization = extractPipeDataVisualizationFromReportData(cached?.reportData);
      if (visualization) {
        setDetailLoading(true);
        setDetailData(visualization);
        setDetailError(null);
        setDetailLoading(false);
        return;
      }

      if (!projectNumber || !entry.docId) {
        setDetailError('Report details are not available for this version.');
        setDetailLoading(false);
        return;
      }

      setDetailLoading(true);
      setDetailData(null);
      setDetailError(null);

      dispatch({
        type: PIPEFITTER_GET_ALL_PIPE_DATA,
        payload: {
          reportHistory: {
            id: entry.historyId ?? entry.docId,
            reportType: PIPEFITTER_REPORT_TYPES.PIPE_DATA,
            reportName: entry.fileName || 'Pipe Data Report',
            projectId,
            projectNumber,
            status: 'Completed',
          },
        },
        cb: (body: { data?: unknown }) => {
          const record = Array.isArray(body?.data) ? body.data[0] : body?.data;
          const reportData =
            record &&
              typeof record === 'object' &&
              'reportData' in record &&
              record.reportData &&
              typeof record.reportData === 'object'
              ? record.reportData
              : record;
          const visualization = extractPipeDataVisualizationFromReportData(reportData);
          setDetailData(visualization);
          if (!hasPipeDataVisualization(visualization)) {
            setDetailError('No pipe data visualization found for this report.');
          }
          setDetailLoading(false);
        },
        err: () => {
          setDetailData(null);
          setDetailError('Could not load pipe data for this report.');
          setDetailLoading(false);
        },
      });
    },
    [dispatch, findHistoryForEntry, projectId, projectNumber]
  );

  const loadCompatibilityDetail = useCallback(
    (entry: GroupedReportEntry) => {
      setDetailLoading(true);
      setDetailData(null);
      setDetailError(null);

      const cachedCompat = resolveCompatibilityFromHistories(entry);
      if (cachedCompat && hasCompatibilityData(cachedCompat)) {
        setDetailData(cachedCompat);
        setDetailLoading(false);
        return;
      }

      if (!projectNumber || !entry.docId) {
        setDetailError('Report details are not available for this version.');
        setDetailLoading(false);
        return;
      }

      const reportHistoryType =
        entry.reportType === PIPEFITTER_REPORT_TYPES.PIPE_DATA
          ? PIPEFITTER_REPORT_TYPES.PIPE_DATA
          : PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY;

      dispatch({
        type: PIPEFITTER_GET_ALL_PIPE_DATA,
        payload: {
          reportHistory: {
            id: entry.historyId ?? entry.docId,
            reportType: reportHistoryType,
            reportName:
              reportHistoryType === PIPEFITTER_REPORT_TYPES.PIPE_DATA
                ? entry.fileName || 'Pipe Data Report'
                : entry.fileName || 'Pipe Compatibility Report',
            projectId,
            projectNumber,
            status: 'Completed',
          },
        },
        cb: (body: { data?: unknown }) => {
          const compat = extractPipeCompatibilityFromBody(body);
          setDetailData(compat);
          if (!hasCompatibilityData(compat)) {
            setDetailError('No compatibility chart found for this report.');
          }
          setDetailLoading(false);
        },
        err: () => {
          setDetailData(null);
          setDetailError('Could not load compatibility data for this report.');
          setDetailLoading(false);
        },
      });
    },
    [dispatch, projectId, projectNumber, resolveCompatibilityFromHistories]
  );

  const openDetail = (
    mode: DetailView['mode'],
    entry: GroupedReportEntry,
    group: ReportHistoryGroup
  ) => {
    setDetailView({ mode, entry, group });
    if (mode === 'pipeData') {
      loadPipeDataDetail(entry);
    } else {
      loadCompatibilityDetail(entry);
    }
  };

  const handleOpenPipeCompatibility = () => {
    if (!detailView || detailView.mode !== 'pipeData') return;
    const compatibilityEntry = detailView.group.pipeCompatibility;
    if (!compatibilityEntry) return;
    openDetail('pipeCompatibility', compatibilityEntry, detailView.group);
  };

  const resolveGenerateIds = useCallback(
    (group: ReportHistoryGroup) => {
      if (selectionIds.length > 0 && pipeIdSetsMatch(group.pipeIds, selectionIds)) {
        return selectionIds;
      }
      return group.pipeIds.length > 0 ? group.pipeIds : selectionIds;
    },
    [selectionIds]
  );

  const canGenerateForGroup = useCallback(
    (group: ReportHistoryGroup) =>
      Boolean(selectedPipe?.pipeId && projectNumber && resolveGenerateIds(group).length > 0),
    [projectNumber, resolveGenerateIds, selectedPipe?.pipeId]
  );

  const openGeneratePipeDataModal = useCallback((pipeStatusIds: string[]) => {
    setCompatibilityGenerate(closedGenerateModal());
    setPipeDataGenerate({ open: true, targetIds: pipeStatusIds });
  }, []);

  const openGenerateCompatibilityModal = useCallback((pipeStatusIds: string[]) => {
    setPipeDataGenerate(closedGenerateModal());
    setCompatibilityGenerate({ open: true, targetIds: pipeStatusIds });
  }, []);

  const handleGeneratePipeDataFromDetail = () => {
    if (!detailView) return;
    openGeneratePipeDataModal(resolveGenerateIds(detailView.group));
  };

  const handleGenerateCompatibilityFromDetail = () => {
    if (!detailView) return;
    openGenerateCompatibilityModal(resolveGenerateIds(detailView.group));
  };

  const resolveExportTarget = useCallback(
    (entry: GroupedReportEntry): { fileUrl: string; fileName: string } | null => {
      const history = findHistoryForEntry(entry);
      const typeRef = history?.fileRefs?.find(
        (ref) => ref.reportType === entry.reportType && ref.fileUrl?.trim()
      );
      if (typeRef?.fileUrl) {
        return {
          fileUrl: typeRef.fileUrl,
          fileName: typeRef.fileName ?? entry.fileName ?? defaultExportFileName(entry),
        };
      }

      const entryUrl = entry.fileUrl?.trim();
      if (entryUrl) {
        const entryRef = history?.fileRefs?.find((ref) => ref.fileUrl === entryUrl);
        if (
          !entryRef?.reportType ||
          entryRef.reportType === entry.reportType ||
          !history?.fileRefs?.length
        ) {
          return {
            fileUrl: entryUrl,
            fileName: entry.fileName || defaultExportFileName(entry),
          };
        }
      }

      if (history?.fileUrl?.trim()) {
        return {
          fileUrl: history.fileUrl.trim(),
          fileName:
            history.fileRefs?.find((ref) => ref.reportType === entry.reportType)?.fileName ??
            entry.fileName ??
            history.reportName ??
            defaultExportFileName(entry),
        };
      }

      const fileRef = history?.fileRefs?.find((ref) => ref.fileUrl?.trim());
      if (fileRef?.fileUrl) {
        return {
          fileUrl: fileRef.fileUrl,
          fileName: fileRef.fileName ?? entry.fileName ?? defaultExportFileName(entry),
        };
      }
      return null;
    },
    [findHistoryForEntry]
  );

  const handleExportReport = async (entry: GroupedReportEntry) => {
    const target = resolveExportTarget(entry);
    if (!target || isExporting) return;
    setIsExporting(true);
    try {
      const res = await fetch(target.fileUrl, { mode: 'cors' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = target.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(target.fileUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setIsExporting(false);
    }
  };

  const closeDetail = () => {
    setDetailView(null);
    setDetailData(null);
    setDetailLoading(false);
    setDetailError(null);
    setIsExporting(false);
  };

  const renderGroupRow = (
    group: ReportHistoryGroup,
    options?: { highlight?: boolean; title?: string }
  ) => {
    const { hasData, hasCompatibility } = groupReportAvailability(group);
    const iconStyles = getGroupIconStyles(group, hasData, hasCompatibility);
    const pipeLabel =
      group.pipeCount > 0
        ? `${group.pipeCount} pipe${group.pipeCount === 1 ? '' : 's'}`
        : 'Report';
    const rowTitle = options?.title ?? getGroupReportTitle(hasData, hasCompatibility);
    const generateIds = resolveGenerateIds(group);
    const pipeDataGenerating = isGroupReportGenerating(
      generateIds,
      PIPEFITTER_REPORT_TYPES.PIPE_DATA,
      histories,
      pendingGenerations
    );
    const compatibilityGenerating = isGroupReportGenerating(
      generateIds,
      PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY,
      histories,
      pendingGenerations
    );
    const missingLabels: string[] = [];
    if (!hasData) missingLabels.push('pipe data');
    if (!hasCompatibility) missingLabels.push('compatibility');

    return (
      <li
        key={group.key}
        className={`flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-page-bg/60 transition-colors ${options?.highlight
          ? 'border-hero-red/30 bg-page-bg/40'
          : 'border-[#E2E5EA]'
          }`}
      >
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconStyles.bg}`}
        >
          {hasData && hasCompatibility ? (
            <div className="flex items-center gap-0.5" aria-hidden>
              <BarChart3 className={`h-4 w-4 ${iconStyles.icon}`} />
              <LineChart className="h-4 w-4 text-hero-red" />
            </div>
          ) : hasCompatibility ? (
            <LineChart className={`h-5 w-5 ${iconStyles.icon}`} aria-hidden />
          ) : (
            <BarChart3 className={`h-5 w-5 ${iconStyles.icon}`} aria-hidden />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-blueprint-blue truncate">{rowTitle}</p>
          <p className="text-[13px] text-heritage-grey mt-0.5">
            {pipeLabel} · {formatGroupDate(group.generatedAt)}
          </p>
          {/* {missingLabels.length > 0 && (
            <p className="text-[12px] text-heritage-grey mt-0.5">
              Missing {missingLabels.join(' & ')} — use Generate below
            </p>
          )} */}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {hasData ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2.5 text-[13px]"
              loading={pipeDataGenerating}
              loadingText="Generating…"
              disabled={pipeDataGenerating}
              onClick={() => {
                if (!pipeDataGenerating && group.pipeData) {
                  openDetail('pipeData', group.pipeData, group);
                }
              }}
            >
              {!pipeDataGenerating ? (
                <BarChart3 className="h-3.5 w-3.5 mr-1.5 text-hero-red" aria-hidden />
              ) : null}
              {pipeDataGenerating ? 'Generating…' : 'Data'}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2.5 text-[13px]"
              disabled={!canGenerateForGroup(group) || generateIds.length === 0 || pipeDataGenerating}
              loading={pipeDataGenerating}
              loadingText="Generating…"
              onClick={() => openGeneratePipeDataModal(generateIds)}
              title={
                pipeDataGenerating
                  ? 'Pipe data report is being generated'
                  : canGenerateForGroup(group) && generateIds.length > 0
                  ? 'Generate pipe data report for this pipe selection'
                  : 'Select pipes in Pipe Details to generate a report'
              }
            >
              {!pipeDataGenerating ? (
                <Plus className="h-3.5 w-3.5 mr-1.5 text-hero-red" aria-hidden />
              ) : null}
              Generate Data
            </Button>
          )}
          {hasCompatibility && group.pipeCompatibility ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2.5 text-[13px]"
              loading={compatibilityGenerating}
              loadingText="Generating…"
              disabled={compatibilityGenerating}
              onClick={() => {
                if (!compatibilityGenerating) {
                  openDetail('pipeCompatibility', group.pipeCompatibility!, group);
                }
              }}
            >
              {!compatibilityGenerating ? (
                <LineChart className="h-3.5 w-3.5 mr-1.5 text-blueprint-blue" aria-hidden />
              ) : null}
              {compatibilityGenerating ? 'Generating…' : 'Compatibility'}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 px-2.5 text-[13px]"
              disabled={
                !canGenerateForGroup(group) || generateIds.length === 0 || compatibilityGenerating
              }
              loading={compatibilityGenerating}
              loadingText="Generating…"
              onClick={() => openGenerateCompatibilityModal(generateIds)}
              title={
                compatibilityGenerating
                  ? 'Pipe compatibility report is being generated'
                  : canGenerateForGroup(group) && generateIds.length > 0
                  ? 'Generate pipe compatibility report for this pipe selection'
                  : 'Select pipes in Pipe Details to generate a report'
              }
            >
              {!compatibilityGenerating ? (
                <Plus className="h-3.5 w-3.5 mr-1.5 text-blueprint-blue" aria-hidden />
              ) : null}
              Generate Compatibility
            </Button>
          )}
        </div>
      </li>
    );
  };

  const selectionGroup = useMemo((): ReportHistoryGroup | null => {
    if (!selectionIds.length) return null;
    if (matchingGroup) return matchingGroup;
    return {
      key: `selection:${normalizePipeIdSetKey(selectionIds)}`,
      pipeCount: selectionIds.length,
      pipeIds: selectionIds,
      generatedAt: new Date(),
      primaryType: PIPEFITTER_REPORT_TYPES.PIPE_DATA,
      pipeData: null,
      pipeCompatibility: null,
    };
  }, [matchingGroup, selectionIds]);

  const selectionRowTitle = useMemo(() => {
    if (!selectionGroup) return '';
    const { hasData, hasCompatibility } = resolveGroupReportAvailability(selectionGroup, histories);
    if (selectionIds.length && !matchingGroup) {
      return `Selected ${selectionGroup.pipeCount} pipe${selectionGroup.pipeCount === 1 ? '' : 's'}`;
    }
    return getGroupReportTitle(hasData, hasCompatibility);
  }, [histories, matchingGroup, selectionGroup, selectionIds.length]);

  const detailTitle = useMemo(() => {
    if (!detailView) return '';
    return detailView.mode === 'pipeData' ? 'Pipe Data Report' : 'Pipe Compatibility Report';
  }, [detailView]);

  const generateModals = (
    <>
      <PipeDataReportGenerateModal
        open={pipeDataGenerate.open}
        onOpenChange={(nextOpen) =>
          setPipeDataGenerate((prev) => ({
            ...prev,
            open: nextOpen,
          }))
        }
        project={project}
        selectedPipe={selectedPipe}
        initialSelectedIds={
          pipeDataGenerate.targetIds.length > 0 ? pipeDataGenerate.targetIds : selectionIds
        }
        onReportGenerated={(pipeStatusIds) =>
          handleReportGenerationStarted(
            PIPEFITTER_REPORT_TYPES.PIPE_DATA,
            pipeStatusIds?.length ? pipeStatusIds : pipeDataGenerate.targetIds.length > 0
              ? pipeDataGenerate.targetIds
              : selectionIds
          )
        }
        dialogClassName={detailView ? PIPEFITTER_FULLSCREEN_DIALOG_Z70 : undefined}
      />
      <PipeCompatibilityReportGenerateModal
        open={compatibilityGenerate.open}
        onOpenChange={(nextOpen) =>
          setCompatibilityGenerate((prev) => ({
            ...prev,
            open: nextOpen,
          }))
        }
        project={project}
        selectedPipe={selectedPipe}
        initialSelectedIds={
          compatibilityGenerate.targetIds.length > 0
            ? compatibilityGenerate.targetIds
            : selectionIds
        }
        onReportGenerated={(pipeStatusIds) =>
          handleReportGenerationStarted(
            PIPEFITTER_REPORT_TYPES.PIPE_COMPATIBILITY,
            pipeStatusIds?.length ? pipeStatusIds : compatibilityGenerate.targetIds.length > 0
              ? compatibilityGenerate.targetIds
              : selectionIds
          )
        }
        dialogClassName={detailView ? PIPEFITTER_FULLSCREEN_DIALOG_Z70 : undefined}
      />
    </>
  );

  if (detailView) {
    const pipeDataDetail =
      detailView.mode === 'pipeData' ? (detailData as ReportHistoryReportData | null) : null;
    const exportTarget = resolveExportTarget(detailView.entry);
    const canExport = Boolean(exportTarget?.fileUrl);
    const hasCompatibilityReport = groupReportAvailability(detailView.group).hasCompatibility;

    return (
      <>
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
          <DialogContent className={PIPEFITTER_FULLSCREEN_DIALOG_Z60}>
            <div className="flex h-screen flex-col overflow-hidden">
              <DialogHeader className="relative z-20 overflow-visible border-b border-[#E2E5EA] bg-white px-4 pb-3 pt-4 sm:px-6 flex-shrink-0">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Button
                      type="button"
                      onClick={closeDetail}
                      className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                      variant="outline"
                      size="icon"
                      aria-label="Back to report history"
                    >
                      <ArrowLeft className="h-4 w-4 text-text-grey" />
                    </Button>
                    <div className="min-w-0">
                      <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                        {detailTitle}
                      </DialogTitle>
                      <p className="text-[13px] text-heritage-grey mt-1">
                        {formatGroupDate(detailView.entry.generatedAt)}
                        {detailView.entry.uploadedBy ? ` · ${detailView.entry.uploadedBy}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="relative z-30 flex items-center gap-2 flex-shrink-0 ml-auto">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleExportReport(detailView.entry)}
                      loading={isExporting}
                      loadingText="Exporting…"
                      disabled={!canExport || detailLoading || isExporting}
                      title={canExport ? 'Download report PDF' : 'Export will be available soon'}
                    >
                      <Download className="h-4 w-4 mr-1.5" aria-hidden />
                      Export
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        className="flex items-center justify-center w-8 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors"
                        aria-label="Report actions"
                      >
                        <MoreHorizontal className="h-4 w-4 text-text-grey" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" disablePortal>
                        {detailView.mode === 'pipeData' ? (
                          <>
                            {hasCompatibilityReport ? (
                              <DropdownMenuItem
                                onClick={handleOpenPipeCompatibility}
                                disabled={detailLoading || isExporting}
                              >
                                <Link2 className="h-4 w-4 mr-2" />
                                Pipe Compatibility
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={handleGenerateCompatibilityFromDetail}
                                disabled={
                                  !canGenerateForGroup(detailView.group) ||
                                  detailLoading ||
                                  isExporting
                                }
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Update Compatibility
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={handleGeneratePipeDataFromDetail}
                              disabled={
                                !canGenerateForGroup(detailView.group) ||
                                detailLoading ||
                                isExporting
                              }
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Update Pipe Data
                            </DropdownMenuItem>
                          </>
                        ) : (
                          <>
                            {detailView.group.pipeData ? (
                              <DropdownMenuItem
                                onClick={() =>
                                  openDetail(
                                    'pipeData',
                                    detailView.group.pipeData!,
                                    detailView.group
                                  )
                                }
                                disabled={detailLoading || isExporting}
                              >
                                <BarChart3 className="h-4 w-4 mr-2 text-hero-red" />
                                Pipe Data
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={handleGeneratePipeDataFromDetail}
                                disabled={
                                  !canGenerateForGroup(detailView.group) ||
                                  detailLoading ||
                                  isExporting
                                }
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Update Pipe Data
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={handleGenerateCompatibilityFromDetail}
                              disabled={
                                !canGenerateForGroup(detailView.group) ||
                                detailLoading ||
                                isExporting
                              }
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Update Compatibility
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                   
                  </div>
                </div>
              </DialogHeader>

              <div
                className={
                  detailView.mode === 'pipeCompatibility'
                    ? 'flex min-h-0 flex-1 w-full flex-col overflow-hidden bg-white'
                    : 'min-h-0 flex-1 w-full overflow-y-auto px-4 py-4 sm:px-6 sm:py-4'
                }
              >
                {detailLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-2 text-heritage-grey">
                    <Loader2 className="h-6 w-6 animate-spin text-hero-red" />
                    <p className="text-[13px]">Loading report…</p>
                  </div>
                ) : detailError ? (
                  <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
                    <AlertCircle className="h-5 w-5 text-hero-red flex-shrink-0" />
                    <span className="text-[13px] text-hero-red">{detailError}</span>
                  </div>
                ) : detailView.mode === 'pipeCompatibility' ? (
                  <div className="flex min-h-0 flex-1 flex-col w-full">
                    <PipeCompatibility
                      projectSummaryData={(detailData as Record<string, unknown>) ?? {}}
                      projectNumber={projectNumber}
                      isLoading={false}
              
                    />
                  </div>
                ) : (
                  <PipeDataReportVisualization reportData={pipeDataDetail} />
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {generateModals}
      </>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden bg-white border border-[#E2E5EA] shadow-[0_1px_3px_rgba(0,0,0,0.04)] rounded-xl [&>button]:hidden">
          <DialogHeader className="px-5 py-4 border-b border-[#E2E5EA] flex flex-row items-start justify-between gap-4 space-y-0">
            <div className="min-w-0 text-left">
              <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                Report History
              </DialogTitle>
              <p className="text-[13px] text-heritage-grey mt-1">
                Previously generated reports for this project.
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-heritage-grey hover:text-blueprint-blue"
              onClick={onClose}
              aria-label="Close report history"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="max-h-[min(70vh,560px)] overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-[13px] text-heritage-grey gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-hero-red" />
                Loading report history…
              </div>
            ) : loadError ? (
              <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
                <AlertCircle className="h-5 w-5 text-hero-red flex-shrink-0" />
                <span className="text-[13px] text-hero-red">{loadError}</span>
              </div>
            ) : groups.length === 0 && !selectionGroup ? (
              <div className="py-10 text-center text-[13px] text-heritage-grey">
                No previous reports generated yet.
              </div>
            ) : (
              <div className="space-y-3">
                <ul className="space-y-3">
                  {selectionGroup &&
                    renderGroupRow(selectionGroup, {
                      highlight: true,
                      title: selectionRowTitle,
                    })}
                  {(selectionGroup ? otherGroups : groups).map((group) => renderGroupRow(group))}
                </ul>
                {hasMoreHistory ? (
                  <div className="pt-1 flex justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 px-3 text-[13px]"
                      disabled={loadingMore}
                      loading={loadingMore}
                      loadingText="Loading…"
                      onClick={() => void loadMoreHistory()}
                    >
                      Load more
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {generateModals}
    </>
  );
};
